import { Check, Zap, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const freeFeatures = ['Tréninkový deník', 'Integrace s hodinkami', 'Plavecký blog', 'Komunita plavců'];

const proFeatures = ['Vše z Free', '12týdenní tréninkové plány', 'Videokurz kraulu', 'Analýza videa a nebo konzultace s trenérem (extra)'];

export default function Pricing() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const scrollToRegistration = () => {
    const element = document.getElementById('download-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      className="py-24 md:py-36 px-4 bg-white relative overflow-hidden"
      ref={ref}
    >
      {/* Background decorations */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="inline-block text-primary font-semibold text-sm uppercase tracking-wider mb-4">
            Ceník
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-foreground">
            Zdarma vs PRO
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            <p className="text-foreground/70 text-lg leading-relaxed text-justify">
              Část aplikace je po jejím stažení zdarma pro všechny.
              Tréninkové plány a pokročilé funkce jsou součástí předplatného.
              Individuální analýza videa a one-on-one konzultace s&nbsp;trenérem se platí zvlášť.
            </p>
            <p className="text-foreground/70 text-lg leading-relaxed text-justify">
              Za cenu jednoho vstupu do aquaparku můžeš mít tréninkový plán na celý měsíc, bez fronty na
              tobogán a s jistotou, že tě každé tempo posune dál. A protože začínáme a chceme, aby vám appka dělala stejnou radost jako nám,
              <span className="font-semibold text-foreground"> prvních 100 plavců bude mít předplatné jen za 199 Kč na měsíc</span> nebo
              <span className="font-semibold text-foreground"> za 499 Kč na čtvrtletí.</span> Pak hladina trochu stoupne na
              <span className="font-semibold text-foreground"> 249 Kč respektive 699 Kč.</span> Skoč do toho, dokud je hladina klidná!
            </p>
          </div>
        </motion.div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free Plan */}
          <motion.div
            className="relative group"
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="h-full p-8 rounded-3xl bg-gradient-to-b from-gray-50 to-white border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2">
              {/* Icon */}
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-6">
                <Zap className="h-7 w-7 text-gray-600" />
              </div>

              <h3 className="text-2xl font-bold mb-2 text-foreground">Swimmie Free</h3>
              <p className="text-foreground/60 mb-8">Zdarma pro všechny</p>

              <ul className="space-y-4 mb-8">
                {freeFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-gray-600" />
                    </div>
                    <span className="text-foreground/80">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={scrollToRegistration}
                variant="outline"
                className="w-full font-semibold py-6 text-base rounded-xl border-2 border-gray-300 hover:border-primary hover:bg-primary hover:text-white transition-all duration-300"
              >
                Stáhnout zdarma
              </Button>
            </div>
          </motion.div>

          {/* Pro Plan */}
          <motion.div
            className="relative group"
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-primary via-blue-400 to-primary rounded-3xl opacity-30 blur-lg group-hover:opacity-50 transition-opacity duration-500" />

            <div className="relative h-full p-8 rounded-3xl bg-gradient-to-b from-primary/5 to-white border-2 border-primary shadow-xl">
              {/* Popular badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                  POPULÁRNÍ
                </span>
              </div>

              {/* Icon */}
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Crown className="h-7 w-7 text-primary" />
              </div>

              <h3 className="text-2xl font-bold mb-2 text-foreground">Swimmie PRO</h3>
              <p className="text-foreground/60 mb-2">Měsíční nebo čtvrtletní předplatné</p>

              {/* Price highlight */}
              <div className="mb-8">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-primary">199 Kč</span>
                  <span className="text-foreground/60">/měsíc</span>
                </div>
                <p className="text-sm text-foreground/50 mt-1">nebo 499 Kč/čtvrtletí</p>
              </div>

              <ul className="space-y-4 mb-8">
                {proFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-primary" />
                    </div>
                    <span className="text-foreground/80">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={scrollToRegistration}
                className="btn-shimmer w-full bg-primary hover:bg-primary/90 text-white font-semibold py-6 text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Stáhnout a vyzkoušet
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
